import { statusToIndicator } from '@common/Execution';
import { DatasetRunType, RunDateType } from '@common/queries/dataset';
import { Entity } from '@common/types/Entity';
import { InterimPortData } from '@common/types/interimTypes';
import { getIDEUrl } from '@common/url';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { negateVariable, theme } from '@prophecy/theme';
import {
  Stack,
  Text,
  Table,
  Checkbox,
  SlidingPanel,
  Button,
  Select,
  Spinner,
  StackItem,
  ColumnsType
} from '@prophecy/ui-v3';
import { Box as ViewBox } from '@prophecy/ui-v3/ViewBox';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { formattedRelativeDateTime } from '@prophecy/utils/date';
import { prettyPrintType } from 'frontend/core/src/schema/sparkSchemaUtils';
import { useState } from 'react';
import styled from 'styled-components';

import { PreviewTable } from '../../../generic-components/PreviewTable';
import { SPARK_COLUMN_TYPES } from '../../../schema/sparkTypes';
import { useRunsData } from '../../Workflow/AttachFabric/componentRunhook';
import { formatCount } from '../util';
import { getLabelTitle } from './Config';

type Props = {
  onClose: () => void;
  entity: Entity;
  uid: string;
  loading: boolean;
  runId: string;
  getDatasetRunDetails: (runId: string, uid: string) => void;
  entityName?: string;
  projectName?: string;
};

const StyledStackItem = styled(StackItem)`
  overflow: auto;
  min-height: 450px;
  height: 100%;
`;

const columns: ColumnsType<StatItem> = [
  { title: 'Field', key: 'field', dataIndex: 'field', render: (value: string) => renderColumnWithEllipsis(value) },
  { title: 'Type', key: 'type', dataIndex: 'type' },
  {
    title: 'Required',
    key: 'required',
    dataIndex: 'required',
    render: (checked: boolean, record: StatItem) => (
      <Checkbox checked={checked} disabled aria-label={`${record.field} required`} />
    )
  },
  {
    title: 'Null %',
    key: 'nullPercent',
    render: (_: string, record) => {
      const { nulls, nonNulls } = record;
      const total = nulls + nonNulls;
      return total ? `${((nulls / total) * 100).toFixed(2)}%` : '0%';
    }
  },
  { title: 'Min', key: 'min', dataIndex: 'min' },
  { title: 'Max', key: 'min', dataIndex: 'max' }
];

const PreviewTitle = styled(Text)`
  margin-bottom: ${negateVariable(theme.sizes.x56)};
`;
type StatItem = {
  type: string;
  key: number;
  field: string;
  nulls: number;
  nonNulls: number;
  min: number;
  max: number;
  mean: number;
  stddev: number;
};
export const Statistics = ({
  onClose,
  entity,
  uid,
  runId,
  entityName,
  projectName,
  loading,
  getDatasetRunDetails
}: Props) => {
  const { datasetRunDetail, connected } = useRunsData({ entity, uid, isMetadataRun: true });

  const {
    interims: interimDataDetail,
    fabric,
    pipelineName,
    recordsProcessed,
    bytes,
    partition,
    pipelineUID,
    componentType,
    status,
    pipelineRun,
    runDates = [] as RunDateType[]
  } = datasetRunDetail || ({} as DatasetRunType);

  const [currentRunId, setRunId] = useState(runId);

  //TODO remove the check once backend fixed it.
  let interim = interimDataDetail && typeof interimDataDetail === 'string' ? JSON.parse(interimDataDetail) : {};
  const {
    schema = { type: SPARK_COLUMN_TYPES.struct, fields: [] },
    data: interimData,
    stats = []
  } = interim as InterimPortData;

  const runSelectOptions = runDates.map((date: RunDateType) => ({
    value: date.uid,
    label: formattedRelativeDateTime(date.submissionTime)
  }));

  const handleRunIdChange = (id: string) => {
    if (id !== currentRunId) {
      setRunId(id);
      getDatasetRunDetails(id, uid);
    }
  };

  const columnTypeMaps = Object.fromEntries(schema.fields.map((field) => [field.name, prettyPrintType(field.type)]));

  const _stats: StatItem[] = stats.map((stat, index) => ({ ...stat, type: columnTypeMaps[stat.field], key: index }));

  const footer = (
    <Stack direction='horizontal' alignY='center' align='space-between'>
      <Button
        variant='primary'
        elementType={pipelineUID ? 'link' : 'button'}
        disabled={!pipelineUID}
        to={pipelineUID ? getIDEUrl({ uid: pipelineUID, entity: Entity.Pipeline }, { 'run-id': pipelineRun?.uid }) : ''}
        data-test-id='statistics-open-pipeline-run-button'>
        Open Pipeline Run
      </Button>
      <Button variant='secondaryGray' onClick={onClose} data-test-id='statistics-close-button'>
        Close
      </Button>
    </Stack>
  );

  return (
    <SlidingPanel footer={footer} open={true} onClose={onClose}>
      <StyledStackItem grow='1' shrink='0'>
        <Stack gap={theme.spaces.x24} height='100%'>
          <Stack width='100%' direction='horizontal' gap={theme.spaces.x8}>
            <Stack gap={theme.spaces.x24} width='20%'>
              {getLabelTitle('Project', projectName || '')}
              {getLabelTitle('Type', componentType)}
            </Stack>
            <Stack gap={theme.spaces.x24} width='20%'>
              {getLabelTitle('Dataset', entityName || '')}
              {getLabelTitle('Row processed', formatCount(recordsProcessed))}
            </Stack>
            <Stack gap={theme.spaces.x24} width='20%'>
              {getLabelTitle('Pipeline', pipelineName, status ? statusToIndicator(status) : '')}
              {getLabelTitle('Bytes Processed', formatCount(bytes))}
            </Stack>
            <Stack gap={theme.spaces.x24} width='20%'>
              {getLabelTitle('Fabric', fabric?.name || '')}
              {getLabelTitle('Partitions Processed', formatCount(partition))}
            </Stack>
            <Stack gap={theme.spaces.x24} width='20%'>
              <Select
                value={currentRunId}
                disabled={loading || !connected}
                loading={connected && loading}
                size='s'
                onChange={handleRunIdChange}
                data-test-id='metadata-statistics-run-id-select'
                label='Run Date*'
                helpText={!connected ? 'Attach cluster to see the dataset run details' : ''}
                options={runSelectOptions}
              />
            </Stack>
          </Stack>
          {loading ? (
            <Spinner tip={loading ? 'Fetching Run Details..' : 'Loading...'} />
          ) : (
            <StyledStackItem grow='1' shrink='0'>
              <Stack gap={theme.spaces.x24} height='100%'>
                <Text level='md'>COLUMN STATISTICS</Text>
                <Table
                  data-test-id='statistics-column-statistics-table'
                  showEmptyMessage={{
                    description: connected
                      ? loading
                        ? 'Loading...'
                        : 'No Data'
                      : 'Attach cluster to see the dataset run details'
                  }}
                  columns={columns}
                  loading={connected && loading}
                  height='100%'
                  dataSource={_stats}
                  fixedHeader={true}
                  virtualize={true}
                />
                <PreviewTitle level='md'>DATA PREVIEW</PreviewTitle>
                <ViewBox height='100%'>
                  <PreviewTable
                    schema={schema}
                    data={interimData || (FROZEN_EMPTY_ARRAY as InterimPortData[])}
                    refreshButton={null}
                    loading={connected && loading}
                  />
                </ViewBox>
              </Stack>
            </StyledStackItem>
          )}
        </Stack>
      </StyledStackItem>
    </SlidingPanel>
  );
};
